import { useState, useEffect, ChangeEvent } from "react";
import { Alert, Dialog, Select, TextInput, Button, Row } from "@narmi/design_system";
import { Case } from "./CaseTable";
import { Payment } from "./PaymentTable";
import { ACWP_REJECT_REASONS, ACWP_REJECT_REASONS_TYPE } from "./codesets";
import { CSRF_TOKEN } from "@pages/csrf";

type ACWPFormData = {
  resolution_reason: string | undefined;
  additional_info: string | undefined;
};

function ACWPDialog({
  caseobj,
  payment,
  action,
  setAction,
  successCallback,
}: {
  caseobj: Case;
  payment: Payment;
  action: "accept" | "reject" | "block" | null;
  setAction: CallableFunction;
  successCallback: CallableFunction;
}) {
  const [formData, setFormData]: [ACWPFormData, CallableFunction] = useState({
    resolution_reason: undefined,
    additional_info: undefined,
  });
  const [error, setError]: [string | null, CallableFunction] = useState(null);
  const [disableSubmit, setDisableSubmit] = useState(false);

  useEffect(() => {
    if (payment && action === "accept") {
      const copy = { ...formData };
      setFormData(copy);
    }
  }, [payment, action]);

  async function onSubmit() {
    setDisableSubmit(true);
    const path = `/v1/fednow/cases/${caseobj.case_identification}/acwp`;
    const res = await fetch(path, {
      method: "POST",
      body: JSON.stringify({ action: action, ...formData }),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": CSRF_TOKEN,
      },
    });
    const resData = await res.json();
    if (!res.ok) {
      if (typeof resData.message === "string") {
        setError(resData.message);
      } else {
        setError(Object.values(resData)[0]);
      }
      setDisableSubmit(false);
      return;
    }
    successCallback();
    setDisableSubmit(false);
    setAction(null);
  }

  return (
    <Dialog
      isOpen={action !== null}
      title={`${action} payment`}
      onUserDismiss={() => setAction(null)}
    >
      {error && (
        <div className="padding--bottom--s">
          <Alert isActive kind="error">
            {error}
          </Alert>
        </div>
      )}

      {(action === "accept" || action === "block") && (
        <p className="padding--bottom--s">
          Are you sure you want to {action} this payment? This action will close the case.
        </p>
      )}

      {action === "reject" && (
        <>
          <div className="padding--bottom--s">
            <Select
              label="Reject reason"
              onChange={(val: string) => {
                const copy = { ...formData };
                copy.resolution_reason = val;
                if (
                  copy.resolution_reason === "NARR" &&
                  (!formData.additional_info || formData.additional_info === "")
                ) {
                  setDisableSubmit(true);
                }
                setFormData(copy);
              }}
            >
              {Object.keys(ACWP_REJECT_REASONS).map((r) => {
                return (
                  <Select.Item value={r}>
                    {ACWP_REJECT_REASONS[r as ACWP_REJECT_REASONS_TYPE]}
                  </Select.Item>
                );
              })}
            </Select>
          </div>

          <div className="padding--bottom--s">
            <TextInput
              label="Additional information"
              error={
                formData.resolution_reason === "NARR" &&
                (!formData.additional_info || formData.additional_info === "")
                  ? "Additional information is required for narrative reason."
                  : null
              }
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const copy = { ...formData };
                copy.additional_info = e.target.value;
                if (formData.resolution_reason === "NARR" && copy.additional_info !== "") {
                  setDisableSubmit(false);
                }
                setFormData(copy);
              }}
            />
          </div>
        </>
      )}

      <div style={{ float: "right" }}>
        <Row alignItems="center">
          <Row.Item shrink>
            <Button label="Cancel" onClick={() => setAction(null)} kind="plain" />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={action || ""}
              style={{ textTransform: "capitalize" }}
              onClick={onSubmit}
              disabled={disableSubmit}
            />
          </Row.Item>
        </Row>
      </div>
    </Dialog>
  );
}

export default ACWPDialog;
